import { useMemo } from 'react';
import moment from 'moment';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFetchOverviewsQuery } from '../service';
import { getOverviewApiStartDate, getOverviewApiEndDate } from '../utils';

type Params = {
    url: string | undefined;
    startDate: moment.Moment;
    endDate: moment.Moment;
    pollingInterval?: number;
};

/**
 * Fetches overviews for a specific date range.
 * Uses the cached data for the month that contains the selected interval
 * Example: for startDate = 10.01.2025, endDate = 24.01.2025, the hook will use cached
 * data for the month of january and filter the result.
 * @param url
 * @param startDate
 * @param endDate
 * @returns
 */
export default function useFetchFilteredOverviews({ url, startDate, endDate, pollingInterval }: Params) {
    const {
        data: overviews,
        isLoading: isLoadingOverviews,
        isFetching: isFetchingOverviews,
        isSuccess: isOverviewsSuccess,
    } = useFetchOverviewsQuery(
        url ? { url, startDate: getOverviewApiStartDate(startDate), endDate: getOverviewApiEndDate(endDate) } : skipToken,
        { pollingInterval }
    );

    const filteredOverviews = useMemo(
        () => (overviews ? overviews.filter((overview) => moment.utc(overview.created_at).isBetween(startDate, endDate, 'day', '[]')) : undefined),
        [endDate, overviews, startDate]
    );

    return {
        data: filteredOverviews,
        isFetching: isFetchingOverviews,
        isLoading: isLoadingOverviews,
        isSuccess: isOverviewsSuccess,
    };
}
